import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarPlus, Download, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { dateOnlyToDate } from "@/lib/format";
import { useDeleteHoliday } from "../../hooks/use-delete-holiday";
import { useHolidays } from "../../hooks/use-holidays";
import { useImportHolidays } from "../../hooks/use-import-holidays";
import type { HolidayItem } from "../../types";
import { timeSettingsErrorMessage } from "../../utils/errors";
import { HolidayDialog } from "../holiday-dialog";

const YEARS_BEFORE = 1;
const YEARS_AFTER = 2;

function yearOptions(current: number, selected: number): number[] {
	const years = new Set<number>();
	for (let y = current - YEARS_BEFORE; y <= current + YEARS_AFTER; y++) {
		years.add(y);
	}
	years.add(selected);
	return [...years].sort((a, b) => a - b);
}

function holidayDateLabel(date: string): string {
	const parsed = dateOnlyToDate(date);
	return parsed ? format(parsed, "dd/MM · EEEE", { locale: ptBR }) : date;
}

export function HolidaysPanel() {
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState(currentYear);
	const [confirmImport, setConfirmImport] = useState(false);
	const [addOpen, setAddOpen] = useState(false);
	const [removing, setRemoving] = useState<HolidayItem | null>(null);

	const holidays = useHolidays(year);
	const importHolidays = useImportHolidays();
	const deleteHoliday = useDeleteHoliday();

	async function handleImport() {
		try {
			const result = await importHolidays.mutateAsync(year);
			toast.success(
				result.count === 1
					? "1 feriado nacional importado"
					: `${result.count} feriados nacionais importados`,
			);
		} catch (err) {
			toast.error(
				timeSettingsErrorMessage(
					err,
					"Não foi possível importar os feriados nacionais.",
				),
			);
		} finally {
			setConfirmImport(false);
		}
	}

	async function handleRemove() {
		if (!removing) return;
		try {
			await deleteHoliday.mutateAsync(removing.id);
			toast.success("Feriado removido");
		} catch (err) {
			toast.error(
				timeSettingsErrorMessage(err, "Não foi possível remover o feriado."),
			);
		} finally {
			setRemoving(null);
		}
	}

	function body() {
		if (holidays.isPending) {
			return (
				<div className="holidays-panel__loading">
					<Loader2 />
				</div>
			);
		}
		if (holidays.isError) {
			return (
				<div className="holidays-panel__empty">
					<p>Não foi possível carregar os feriados.</p>
					<Button
						variant="outline"
						size="sm"
						onClick={() => holidays.refetch()}
					>
						Tentar novamente
					</Button>
				</div>
			);
		}
		if (holidays.data.length === 0) {
			return (
				<div className="holidays-panel__empty">
					<p>Nenhum feriado cadastrado em {year}.</p>
				</div>
			);
		}
		return (
			<ul className="holidays-list">
				{holidays.data.map((holiday) => (
					<li
						key={holiday.id}
						className="holidays-list__item"
						data-source={holiday.source}
					>
						<span className="holidays-list__date">
							{holidayDateLabel(holiday.date)}
						</span>
						<span className="holidays-list__name">{holiday.name}</span>
						{holiday.source === "national" ? (
							<Badge variant="secondary">Nacional</Badge>
						) : (
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="holidays-list__remove"
								onClick={() => setRemoving(holiday)}
							>
								<Trash2 />
								Remover
							</Button>
						)}
					</li>
				))}
			</ul>
		);
	}

	return (
		<Card className="holidays-panel">
			<CardHeader>
				<CardTitle>Feriados</CardTitle>
				<CardDescription>
					Feriados nacionais valem para toda a conta; os manuais só para este
					canteiro.
				</CardDescription>
				<div className="holidays-panel__toolbar">
					<Select
						value={String(year)}
						onValueChange={(value) => setYear(Number(value))}
					>
						<SelectTrigger
							size="sm"
							className="holidays-panel__year"
							aria-label="Ano"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{yearOptions(currentYear, year).map((option) => (
								<SelectItem key={option} value={String(option)}>
									{option}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => setConfirmImport(true)}
						disabled={importHolidays.isPending}
					>
						<Download />
						Importar feriados nacionais
					</Button>
					<Button type="button" size="sm" onClick={() => setAddOpen(true)}>
						<CalendarPlus />
						Adicionar feriado
					</Button>
				</div>
			</CardHeader>
			<CardContent>{body()}</CardContent>

			<ConfirmDialog
				open={confirmImport}
				onOpenChange={setConfirmImport}
				title={`Importar feriados nacionais de ${year}?`}
				description="Os feriados nacionais do ano são adicionados ao calendário da conta. Feriados já importados não são duplicados."
				confirmLabel="Importar"
				onConfirm={handleImport}
				isPending={importHolidays.isPending}
			/>

			<ConfirmDialog
				open={removing != null}
				onOpenChange={(open) => {
					if (!open) setRemoving(null);
				}}
				title="Remover feriado?"
				description={
					removing
						? `${removing.name} (${holidayDateLabel(removing.date)}) deixará de contar como feriado neste canteiro.`
						: ""
				}
				confirmLabel="Remover feriado"
				onConfirm={handleRemove}
				destructive
				isPending={deleteHoliday.isPending}
			/>

			<HolidayDialog
				open={addOpen}
				onOpenChange={setAddOpen}
				year={year}
				onCreated={setYear}
			/>
		</Card>
	);
}
